import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { getServiceAreas, createServiceArea, updateServiceArea, deleteServiceArea } from '../services/api';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, MapPin } from 'lucide-react';

const AdminServiceAreas = () => {
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingArea, setEditingArea] = useState(null);
  const [formData, setFormData] = useState({
    name_en: '', name_zh: '', active: true
  });

  const fetchAreas = async () => {
    try {
      const res = await getServiceAreas();
      setAreas(res.data);
    } catch (error) {
      toast.error('Failed to load service areas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAreas();
  }, []);

  const handleEdit = (area) => {
    setEditingArea(area);
    setFormData(area);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingArea(null);
    setFormData({ name_en: '', name_zh: '', active: true });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      if (editingArea) {
        await updateServiceArea(editingArea.id, formData);
        toast.success('Service area updated');
      } else {
        await createServiceArea(formData);
        toast.success('Service area created');
      }
      fetchAreas();
      setShowModal(false);
    } catch (error) {
      toast.error('Failed to save service area');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service area?')) return;
    try {
      await deleteServiceArea(id);
      setAreas(areas.filter(a => a.id !== id));
      toast.success('Service area deleted');
    } catch (error) {
      toast.error('Failed to delete service area');
    }
  };

  const handleToggleActive = async (area) => {
    try {
      await updateServiceArea(area.id, { active: !area.active });
      setAreas(areas.map(a => a.id === area.id ? { ...a, active: !a.active } : a));
    } catch (error) {
      toast.error('Failed to update service area');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Service Areas</h1>
          <p className="text-slate-600">Manage locations where you provide services</p>
        </div>
        <Button onClick={handleCreate} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
          <Plus className="w-4 h-4 mr-2" /> Add Area
        </Button>
      </div>

      <Card className="p-6 bg-white border-2 border-slate-300">
        <div className="flex flex-wrap gap-3">
          {areas.map((area) => (
            <div
              key={area.id}
              className={`flex items-center gap-2 bg-slate-100 px-4 py-2 rounded-full group ${!area.active ? 'opacity-50' : ''}`}
            >
              <MapPin className="w-4 h-4 text-amber-500" />
              <span className="font-medium text-slate-900">{area.name_en}</span>
              <span className="text-slate-400">|</span>
              <span className="text-slate-600">{area.name_zh}</span>
              <div className="hidden group-hover:flex items-center gap-1 ml-2">
                <Switch
                  checked={area.active}
                  onCheckedChange={() => handleToggleActive(area)}
                  className="scale-75"
                />
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdit(area)}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-6 w-6 text-red-500" onClick={() => handleDelete(area.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Full list view */}
      <Card className="p-6 bg-white border-2 border-slate-300">
        <h3 className="font-semibold text-slate-900 mb-4">All Service Areas</h3>
        <div className="space-y-2">
          {areas.map((area) => (
            <div
              key={area.id}
              className={`flex items-center justify-between p-3 bg-slate-50 rounded-lg ${!area.active ? 'opacity-50' : ''}`}
            >
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-amber-500" />
                <div>
                  <span className="font-medium text-slate-900">{area.name_en}</span>
                  <span className="text-slate-400 mx-2">|</span>
                  <span className="text-slate-600">{area.name_zh}</span>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={area.active} onCheckedChange={() => handleToggleActive(area)} />
                <Button variant="outline" size="sm" onClick={() => handleEdit(area)}>
                  <Pencil className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" className="text-red-500" onClick={() => handleDelete(area.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Area Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingArea ? 'Edit Service Area' : 'Add New Service Area'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Area Name (English)</label>
              <Input
                value={formData.name_en}
                onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                placeholder="e.g., Richmond Hill"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Area Name (Chinese)</label>
              <Input
                value={formData.name_zh}
                onChange={(e) => setFormData({ ...formData, name_zh: e.target.value })}
                placeholder="e.g., 列治文山"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                checked={formData.active}
                onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
              />
              <span className="text-sm">Active (visible on website)</span>
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-slate-900">
                Save Area
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminServiceAreas;
